import { FC } from "react";
import { Modal, TextInput, Button } from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { useSnackbar } from "notistack";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CaseStatusApi from "@/api/case-status";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import {
  FormWrapper,
  FormTitle,
} from "@/components/Forms/components/MonolithFormComponents";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

interface CreateCaseStatusModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

export const CreateCaseStatusModal: FC<CreateCaseStatusModalProps> = ({
  show,
  onClose,
  onSubmit,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: {
      status_name: "",
    },
    validate: {
      status_name: (value) =>
        !value || value.trim() === "" ? "Case Status is required" : null,
    },
  });

  const createMutation = useMutation({
    mutationFn: (params: { status_name: string }) =>
      CaseStatusApi.createCaseStatus(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-statuses", "list"] });

      enqueueSnackbar(`Case Status "${form.values.status_name}" created`, {
        variant: "success",
      });

      LoggingAPI.createLog({
        user_id: currentUser.user_id,
        message: `Created Case Status: ${form.values.status_name}`,
        event: "case_status:create",
        object_type: "case_status",
      });

      form.reset();
      onSubmit?.();
      onClose();
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar(
        "An error occurred while creating this case status. Please try again.",
        { variant: "error" }
      );
    },
  });

  const handleSubmit = () => {
    if (!form.validate().hasErrors) {
      createMutation.mutate({ status_name: form.values.status_name });
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal open={show} onClose={handleCancel}>
      <FormWrapper width={500}>
        <FormTitle>Create New Case Status</FormTitle>
        <TextInput
          size="sm"
          variant="outlined"
          label="Case Status"
          required
          placeholder="Enter case status name"
          description="The name of the case status"
          {...form.getInputProps("status_name")}
        />
        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            Create Case Status
          </Button>
        </ButtonMenu>
      </FormWrapper>
    </Modal>
  );
};
